// src/components/CatalogView.jsx
import React, { useState, useEffect, memo, useCallback } from 'react';
import Header from './Header';
import Item from './Item';
import SearchAddBar from './SearchAddBar';
import SearchOverlay from './SearchOverlay';
import CatalogStateBar from './CatalogStateBar';
import QuickEditModal from './QuickEditModal';
import ConfirmModal from './ConfirmModal';
import { useGroceries } from '../hooks/useGroceries';
import { useCategories } from '../hooks/useCategories';
import { useItemOperations } from '../hooks/useItemOperations';
import { useItemFilters } from '../hooks/useItemFilters';
import { useListCalculations } from '../hooks/useListCalculations';
import { getTodayDateString } from '../utils/itemUtils';

const STORAGE_KEY = 'catalog_sort_preference';
const COLLAPSED_CATEGORIES_KEY = 'collapsed_categories_catalog';

const CatalogView = memo(({
  showToast,
  lists,
  activeList,
  activeListId,
  onRefreshLists
}) => {
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'Long-ago purchased';
  });
  
  // Estado para modal de selección de lista al agregar a lista
  const [showListModal, setShowListModal] = useState(false);
  const [addingToListItem, setAddingToListItem] = useState(null);
  
  // Estado para modal de change list
  const [showChangeListModal, setShowChangeListModal] = useState(false);
  const [changingListItem, setChangingListItem] = useState(null);
  
  // Estado para categorías colapsadas
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    const saved = localStorage.getItem(COLLAPSED_CATEGORIES_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    variant: 'primary',
    onConfirm: null,
  });

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  const { allItems, loading, error, fetchItems, addNewItem, updateItem, deleteItem, setAllItems } = useGroceries(showToast);
  const { availableCategories, loading: categoriesLoading, error: categoriesError, fetchCategories, updateCategories, addNewCategory } = useCategories(showToast);
  const { checkToggleItem, updateQuantity, addToList, removeFromList, updateName, updatePrice, updateNote, updateCategory, updateList } = useItemOperations(showToast);
  const { groupItemsByCategory, getItemsInListCount, isItemInList } = useItemFilters();
  const { sortCatalogItems } = useListCalculations();

  // Guardar preferencia de sort en localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, sortBy);
  }, [sortBy]);

  // Guardar estado de categorías colapsadas en localStorage
  useEffect(() => {
    localStorage.setItem(COLLAPSED_CATEGORIES_KEY, JSON.stringify(collapsedCategories));
  }, [collapsedCategories]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchItems(), fetchCategories()]);
    };
    loadData();
  }, [fetchItems, fetchCategories]);

  // Aplicar ordenamiento a TODOS los items
  const sortedAllItems = sortCatalogItems(allItems, sortBy);

  const { grouped: groupedItems, sortedCategories } = groupItemsByCategory(sortedAllItems, availableCategories);
  const itemsInList = getItemsInListCount(allItems);
  const itemsInCatalog = allItems.length;

  const handleRefresh = () => {
    fetchItems();
    fetchCategories();
    showToast('Refreshing...', 'info');
  };

  const handleSettings = () => {
    showToast('Settings feature coming soon', 'info');
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const toggleCategory = useCallback((category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  const handleUpdateItem = async (itemId, updateData) => {
    const previousItems = [...allItems];
    
    try {
      setAllItems(prev => prev.map(item => item.id === itemId ? { ...item, ...updateData } : item));

      let updatedData;
      const itemToUpdate = allItems.find(item => item.id === itemId);
      
      if ('item' in updateData) {
        updatedData = await updateName(itemId, updateData.item, itemToUpdate);
      } else if ('price' in updateData) {
        updatedData = await updatePrice(itemId, updateData.price, itemToUpdate);
      } else if ('category' in updateData) {
        updatedData = await updateCategory(itemId, updateData.category, itemToUpdate);
        if (updateData.category && !availableCategories.includes(updateData.category)) {
          addNewCategory(updateData.category);
        }
      } else if ('note' in updateData) {
        updatedData = await updateNote(itemId, updateData.note, itemToUpdate);
      } else if ('list_id' in updateData) {
        updatedData = await updateList(itemId, updateData.list_id, itemToUpdate);
      } else {
        throw new Error('Invalid update data');
      }

      setAllItems(prev => prev.map(item => item.id === itemId ? { ...item, ...updatedData } : item));

    } catch (err) {
      console.error('Error updating item:', err);
      setAllItems(previousItems);
      showToast(`Error updating: ${err.message}`, 'error');
    }
  };

  const handleCheckToggle = async (id) => {
    const previousItems = [...allItems];
    
    try {
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      const currentStatus = parseInt(itemToUpdate.status);
      const newStatus = currentStatus === 1 ? 0 : 1;
      const newDate = newStatus === 1 ? getTodayDateString() : itemToUpdate.date;
      
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        status: newStatus.toString(), 
        date: newDate 
      } : item));
      
      const updatedData = await checkToggleItem(itemToUpdate);
      
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        status: updatedData.status, 
        date: updatedData.date || item.date 
      } : item));
      
    } catch (err) {
      console.error('Error toggling item:', err);
      setAllItems(previousItems);
      showToast(`Error toggling item: ${err.message}`, 'error');
    }
  };

  const handleQuantityChange = async (id, newQuantity) => {
    const previousItems = [...allItems];
    
    try {
      setAllItems(prev => prev.map(item => item.id === id ? { ...item, quantity: newQuantity } : item));
      
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      await updateQuantity(itemToUpdate, newQuantity);
      
    } catch (err) {
      console.error('Error updating quantity:', err);
      setAllItems(previousItems);
      showToast(`Error updating quantity: ${err.message}`, 'error');
    }
  };

  const handleRemoveFromList = async (id) => {
    const previousItems = [...allItems];
    
    try {
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        quantity: 0, 
        list_id: null,
        list_name: ''
        // NO modificar date - se mantiene la original desde removeFromList
      } : item));
      
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      await removeFromList(itemToUpdate);
      showToast('Item removed from list', 'success');
      if (onRefreshLists) onRefreshLists();
      
    } catch (err) {
      console.error('Error removing from list:', err);
      setAllItems(previousItems);
      showToast(`Error removing from list: ${err.message}`, 'error');
    }
  };

  const handleAddToList = (id) => {
    const itemToAdd = allItems.find(item => item.id === id);
    if (itemToAdd) {
      setAddingToListItem(itemToAdd);
      setShowListModal(true);
    }
  };

  const handleAddToListSave = async (updateData) => {
    if (!addingToListItem) return;
    
    const previousItems = [...allItems];
    const selectedListId = updateData.list_id;
    const selectedListName = updateData.list_name || '';
    
    if (!selectedListId) {
      showToast('Please select a list', 'error');
      return;
    }
    
    try {
      setAllItems(prev => prev.map(item => item.id === addingToListItem.id ? { 
        ...item, 
        quantity: 1, 
        status: "0",
        list_id: selectedListId,
        list_name: selectedListName
      } : item));
      
      await addToList(addingToListItem, selectedListId);
      showToast(`Item added to ${selectedListName || 'list'}`, 'success');
      setShowListModal(false);
      setAddingToListItem(null);
      if (onRefreshLists) onRefreshLists();
      
    } catch (err) {
      setAllItems(previousItems);
      showToast(`Error: ${err.message}`, 'error');
    }
  };

  const handleChangeList = (item) => {
    setChangingListItem(item);
    setShowChangeListModal(true);
  };

  const handleChangeListSave = async (updateData) => {
    if (!changingListItem) return;
    
    try {
      const newListId = updateData.list_id;
      if (!newListId) throw new Error('List is required');
      
      await handleUpdateItem(changingListItem.id, {
        list_id: newListId,
        list_name: updateData.list_name || ''
      });
      setShowChangeListModal(false);
      setChangingListItem(null);
      showToast(`Item moved to ${updateData.list_name || 'list'}`, 'success');
      if (onRefreshLists) onRefreshLists();
      
    } catch (err) {
      showToast(`Error changing list: ${err.message}`, 'error');
    }
  };

  const doDeleteItem = async (id) => {
    const previousItems = [...allItems];
    try {
      setAllItems(prev => prev.filter(item => item.id !== id));
      await deleteItem(id);
      showToast('Item deleted', 'success');
    } catch (err) {
      console.error('Error deleting item:', err);
      setAllItems(previousItems);
      showToast(`Error deleting item: ${err.message}`, 'error');
    }
  };

  const handleDeleteItem = (id) => {
    const target = allItems.find(it => it.id === id);
    const itemName = target?.item || 'this item';
    setConfirmDialog({
      isOpen: true,
      title: 'Delete item',
      message: (
        <>
          Are you sure you want to delete <strong>{itemName}</strong>?
        </>
      ),
      confirmLabel: 'Delete',
      variant: 'danger',
      onConfirm: () => {
        closeConfirmDialog();
        doDeleteItem(id);
      },
    });
  };

  const handleAddToCatalog = async (itemData) => {
    try {
      const newItem = await addNewItem(itemData, showToast);
      
      if (newItem.category && !availableCategories.includes(newItem.category)) {
        addNewCategory(newItem.category);
      }
      
      return newItem;
    } catch (err) {
      throw err;
    }
  };

  const handleSearchFocus = () => {
    setShowSearchOverlay(true);
  };

  const isLoading = loading || categoriesLoading;
  const hasError = error || categoriesError;

  const renderCategoryGroups = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-3"></div>
            <p>Loading catalog...</p>
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="p-8 text-center">
          <div className="text-red-600 mb-2">⚠️ {error || categoriesError}</div>
          <button 
            onClick={() => {
              fetchItems();
              fetchCategories();
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (Object.keys(groupedItems).length === 0) {
      return (
        <div className="p-8 text-center text-gray-500">
          <div className="mb-6">
            <i className="fas fa-book text-gray-300 text-6xl mx-auto mb-4 block"></i>
            <p className="mb-4">Your catalog is empty</p>
            <p className="text-sm text-gray-600 mb-4">
              Use the search bar above to add items
            </p>
          </div>
        </div>
      );
    }

    return sortedCategories.map(category => {
      const isCollapsed = collapsedCategories[category] || false;
      const displayCategory = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
      
      return (
        <div key={category} className="mb-0">
          {/* Category Header - Clickable */}
          <div 
            className="sticky top-0 bg-gray-200 px-3 py-2 border-b border-gray-200 z-10 flex justify-between items-center cursor-pointer hover:bg-gray-300 transition-colors"
            onClick={() => toggleCategory(category)}
          >
            <h3 className="font-semibold text-gray-700 text-sm tracking-wider">
              {displayCategory}
            </h3>
            <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'} text-gray-500 text-xs transition-transform`}></i>
          </div>
          
          {/* Category Items - Collapsible */}
          {!isCollapsed && (
            <div>
              {groupedItems[category].map(item => {
                const hasList = item.list_id !== null && item.list_id !== undefined;
                const isUnchecked = parseInt(item.status) === 0;
                const inList = hasList && isUnchecked;
                return (
                  <Item
                    key={item.id}
                    item={item}
                    isInList={inList}
                    showDaysSince={!inList}
                    onCheckToggle={handleCheckToggle}
                    onDelete={handleDeleteItem}
                    onEdit={(item) => handleUpdateItem(item.id, { item: item.item })}
                    onQuantityChange={handleQuantityChange}
                    onAddToList={handleAddToList}
                    onRemoveFromList={handleRemoveFromList}
                    onChangeList={inList ? handleChangeList : undefined}
                    showToast={showToast}
                    onUpdateItem={handleUpdateItem}
                    availableCategories={availableCategories}
                    currentView="catalog"
                    activeList={activeList}
                  />
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-32">
      <Header
        title="Catalog"
        showCatalogButton={false}
        onRefresh={handleRefresh}
        onSettings={handleSettings}
      />

      {/* Search bar */}
      <div className="p-2 bg-white border-b border-gray-200">
        <SearchAddBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onAdd={(term) => {
            setSearchTerm(term);
            setShowSearchOverlay(true);
          }}
          onFocus={handleSearchFocus}
          onClear={() => setSearchTerm('')}
          isSaving={false}
          placeholder="Type to search or add items"
        />
      </div>

      {/* Catalog Summary Panel con Sort by */}
      <CatalogStateBar
        currentSort={sortBy}
        onSortChange={handleSortChange}
      />

      <div className="divide-y divide-gray-100 pb-6">
        {renderCategoryGroups()}
      </div>

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={showSearchOverlay}
        onClose={() => setShowSearchOverlay(false)}
        context="catalog"
        allItems={allItems}
        onAddToList={handleAddToList}
        onAddToCatalog={handleAddToCatalog}
        onEditItem={(item) => handleUpdateItem(item.id, { item: item.item })}
        onDeleteItem={handleDeleteItem}
        onQuantityChange={handleQuantityChange}
        onCheckToggle={handleCheckToggle}
        onChangeList={handleChangeList}
        onUpdateItem={handleUpdateItem}
        showToast={showToast}
        availableCategories={availableCategories}
        activeList={activeList}
        activeListId={activeListId}
        lists={lists}
      />

      {/* Modal para seleccionar lista al agregar a lista desde catálogo */}
      {showListModal && (
        <QuickEditModal
          isOpen={showListModal}
          onClose={() => {
            setShowListModal(false);
            setAddingToListItem(null);
          }}
          field="list"
          context="list"
          currentValue=""
          itemId={addingToListItem?.id}
          itemName={addingToListItem?.item}
          onSaveSuccess={handleAddToListSave}
          showToast={showToast}
          availableLists={lists}
        />
      )}

      {/* Modal para Change list */}
      {showChangeListModal && (
        <QuickEditModal
          isOpen={showChangeListModal}
          onClose={() => {
            setShowChangeListModal(false);
            setChangingListItem(null);
          }}
          field="list"
          context="list"
          currentValue=""
          itemId={changingListItem?.id}
          itemName={changingListItem?.item}
          onSaveSuccess={handleChangeListSave}
          showToast={showToast}
          availableLists={lists}
        />
      )}

      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
      />
    </div>
  );
});

export default CatalogView;